/**
 * Validation utilities for facturation module
 */

/**
 * Email validation regex
 * RFC 5322 compliant basic validation
 */
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate email format
 * @param email - Email address to validate
 * @returns True if valid, false otherwise
 */
export const isValidEmail = (email: string | null | undefined): boolean => {
  if (!email || typeof email !== 'string') {
    return false;
  }

  const trimmedEmail = email.trim();
  if (trimmedEmail.length === 0) {
    return false;
  }

  return EMAIL_REGEX.test(trimmedEmail);
};

/**
 * Validate and normalize email
 * @param email - Email address to validate
 * @param required - Whether email is required
 * @returns Validation result with normalized email
 */
export const validateEmail = (
  email: string | null | undefined,
  required = false
): { valid: boolean; email: string | null; error: string | null } => {
  // If email is not provided
  if (!email || (typeof email === 'string' && email.trim().length === 0)) {
    if (required) {
      return {
        valid: false,
        email: null,
        error: 'L\'adresse email est obligatoire',
      };
    }
    return {
      valid: true,
      email: null,
      error: null,
    };
  }

  // Validate format
  if (!isValidEmail(email)) {
    return {
      valid: false,
      email: null,
      error: 'Format d\'email invalide. Veuillez fournir une adresse email valide (ex: nom@exemple.com)',
    };
  }

  return {
    valid: true,
    email: email.trim().toLowerCase(),
    error: null,
  };
};

/**
 * Normalize a raw VAT number for form prefill.
 * FM stores numero_tva without country code and with junk values ("NA", "0..").
 * Mirrors backend PeppolService.normalizeVatNumber, but returns '' for junk
 * so we never prefill an invalid value.
 * @param vatNumber - Raw VAT number (e.g., "1021488588", "BE 1021.488.588", "NA")
 * @returns Normalized VAT number (e.g., "BE1021488588") or '' if unusable
 */
export const normalizeVatNumberForPrefill = (vatNumber: string | null | undefined): string => {
  if (!vatNumber) return '';
  const cleaned = String(vatNumber).replace(/[\s.\-]/g, '').toUpperCase();
  if (/^[A-Z]{2}[0-9A-Z]{8,12}$/.test(cleaned)) return cleaned;
  if (/^\d{10}$/.test(cleaned)) return 'BE' + cleaned;
  if (/^\d{9}$/.test(cleaned)) return 'BE0' + cleaned;
  return '';
};

/**
 * Validate phone number (basic validation)
 * @param phone - Phone number to validate
 * @returns True if valid format, false otherwise
 */
export const isValidPhone = (phone: string | null | undefined): boolean => {
  if (!phone || typeof phone !== 'string') {
    return false;
  }

  const trimmedPhone = phone.trim();
  if (trimmedPhone.length === 0) {
    return false;
  }

  // Basic phone validation: at least 8 digits, can contain +, spaces, dashes, parentheses
  const phoneRegex = /^[\d\s()+-]{8,}$/;
  return phoneRegex.test(trimmedPhone);
};

/**
 * Belgian Structured Communication Validation
 * Format: +++XXX/XXXX/XXXXX+++ where the last 2 digits are a modulo 97 checksum
 */

/**
 * Validate a Belgian structured communication reference
 * @param reference - The reference to validate (e.g., "+++123/4567/89012+++" or "12345678901")
 * @returns True if valid, false otherwise
 */
export const isValidBelgianStructuredCommunication = (reference: string | null | undefined): boolean => {
  if (!reference || typeof reference !== 'string') {
    return false;
  }

  // Remove +++ delimiters and / separators
  const cleaned = reference.replace(/[+/\s]/g, '');

  // Must be exactly 12 digits
  if (!/^\d{12}$/.test(cleaned)) {
    return false;
  }

  // First 10 digits are the base number
  const baseNumber = parseInt(cleaned.substring(0, 10), 10);
  // Last 2 digits are the checksum
  const providedChecksum = parseInt(cleaned.substring(10, 12), 10);

  // Calculate expected checksum using modulo 97
  // Special case: if result is 0, use 97
  const expectedChecksum = baseNumber % 97 || 97;

  return providedChecksum === expectedChecksum;
};

/**
 * Generate a valid Belgian structured communication reference
 * @param baseNumber - The base number to use (will be padded/truncated to 10 digits)
 * @returns Formatted reference in +++XXX/XXXX/XXXXX+++ format
 */
export const generateBelgianStructuredCommunication = (baseNumber: string | number): string => {
  // Convert to string and remove non-numeric characters
  const numericPart = String(baseNumber).replace(/\D/g, '');

  // Pad to 10 digits (or truncate if longer)
  const paddedNumber = numericPart.padStart(10, '0').slice(-10);

  // Calculate modulo 97 check digit
  const base = parseInt(paddedNumber, 10);
  let checkDigit = base % 97;
  if (checkDigit === 0) checkDigit = 97;

  // Combine base number with checksum
  const fullNumber = paddedNumber + String(checkDigit).padStart(2, '0');

  // Format: +++XXX/XXXX/XXXXX+++
  return `+++${fullNumber.slice(0, 3)}/${fullNumber.slice(3, 7)}/${fullNumber.slice(7)}+++`;
};

/**
 * Parse and validate a structured communication, returning the cleaned version
 * @param reference - The reference to parse
 * @returns Validation result with cleaned and formatted versions
 */
export const parseStructuredCommunication = (
  reference: string | null | undefined
): { valid: boolean; cleaned: string | null; formatted: string | null; error: string | null } => {
  if (!reference || typeof reference !== 'string') {
    return {
      valid: false,
      cleaned: null,
      formatted: null,
      error: 'La référence structurée est requise',
    };
  }

  // Remove +++ delimiters and / separators
  const cleaned = reference.replace(/[+/\s]/g, '');

  // Must be exactly 12 digits
  if (!/^\d{12}$/.test(cleaned)) {
    return {
      valid: false,
      cleaned: null,
      formatted: null,
      error: 'La référence structurée doit contenir exactement 12 chiffres (format: +++XXX/XXXX/XXXXX+++)',
    };
  }

  // Validate checksum
  const baseNumber = parseInt(cleaned.substring(0, 10), 10);
  const providedChecksum = parseInt(cleaned.substring(10, 12), 10);
  const expectedChecksum = baseNumber % 97 || 97;

  if (providedChecksum !== expectedChecksum) {
    return {
      valid: false,
      cleaned,
      formatted: null,
      error: `Checksum invalide. Attendu: ${String(expectedChecksum).padStart(2, '0')}, fourni: ${String(
        providedChecksum
      ).padStart(2, '0')}`,
    };
  }

  // Format with delimiters
  const formatted = `+++${cleaned.slice(0, 3)}/${cleaned.slice(3, 7)}/${cleaned.slice(7)}+++`;

  return {
    valid: true,
    cleaned,
    formatted,
    error: null,
  };
};

/**
 * Calculate the checksum for a 10-digit base number
 * @param baseNumber - The 10-digit base number
 * @returns The checksum (1-97)
 */
export const calculateChecksum = (baseNumber: string | number): number => {
  const cleaned = String(baseNumber).replace(/\D/g, '').slice(0, 10).padStart(10, '0');
  const base = parseInt(cleaned, 10);
  return base % 97 || 97;
};
